import {DEFAULT_FORM_VALUES, FormValuesProps} from "../modals/notificationModals/NewNotificationModal";
import Step1Fields from "../messageCreationFlow/step1/Step1Fields";
import Step2Fields from "../messageCreationFlow/step2/Step2Fields";
import Step3Fields from "../messageCreationFlow/step3/Step3Fields";
import "./one-step-template-creation.css";
import {Button} from "antd";
import {ThunderboltFilled} from "@ant-design/icons";
import MessagePreview from "../messagePreview/MessagePreview";
import useUserContext from "../../hooks/useUserContext";
import {useEffect, useState} from "react";
import useToast from "../../hooks/useToast";
import {useNavigate} from "react-router-dom";
import {createNotification} from "../../api/createNotification";
import useAppContext from "../../hooks/useAppContext";
import LongArrowLeft from "../../assets/icons/LongArrowLeft";
import {LANGUAGES} from "../../utils/languages";
import {createCampaignNotification} from "../../api/createCampaignNotification";
import {createCampaign} from "../../api/createCampaign";
import {ToastSkin} from "../../context/ToastContext";

interface OneStepTemplateCreationProps {
    flowId?: number;
    isCampaign?: boolean;
    onTemplateCreated: (newId: number, parentId: number) => void;
    onCancel: () => void;
    flowTriggerId?: number;
}

const OneStepTemplateCreation = ({ flowId, isCampaign = false, onTemplateCreated, onCancel, flowTriggerId }: OneStepTemplateCreationProps) => {
    const [formValues, setFormValues] = useState<FormValuesProps>(isCampaign ? { ...DEFAULT_FORM_VALUES, isWithImage: false, isActive: true, button1Text: 'Shop Now' } : DEFAULT_FORM_VALUES);
    const [isDefaultDataLoaded, setIsDefaultDataLoaded] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const { selectedDomain } = useUserContext();
    const { actions: { loadFlows, loadCampaigns }} = useAppContext();
    const { showToast, closeToast } = useToast();
    const navigate = useNavigate();

    const setTitle = (newValue: string) => setFormValues({ ...formValues, title: newValue });
    const setLanguage = (newValue: string) => setFormValues({ ...formValues, language: newValue });
    const setContent = (newValue: string) => setFormValues({ ...formValues, content: newValue });
    const setIsWithImage = (newValue: boolean) => setFormValues({ ...formValues, isWithImage: newValue });
    const setIsCoupon = (newValue: boolean) => setFormValues({ ...formValues, isCoupon: newValue });
    const setTriggerDelay = (newValue: number | null) => setFormValues({ ...formValues, triggerDelay: newValue });
    const setIsActive = (newValue: boolean) => setFormValues({ ...formValues, isActive: newValue });
    const setButton1Text = (newValue: string) => setFormValues({ ...formValues, button1Text: newValue });
    const setButton2Text = (newValue: string) => setFormValues({ ...formValues, button2Text: newValue });
    const setRequiresConsent = (newValue: boolean) => setFormValues({ ...formValues, requiresConsent: newValue });
    const setImageUrl = (newValue: string) => setFormValues({ ...formValues, imageUrl: newValue });
    const setButton1Url = (newValue: string) => setFormValues({ ...formValues, button1Url: newValue });

    const selectedLanguage = LANGUAGES.find(lang => lang.key === formValues.language);
    const direction = selectedLanguage?.direction === 'rtl' ? 'rtl' : 'ltr';
    const shouldAskForDelay = !isCampaign || (!!flowTriggerId && flowTriggerId !== 11);

    const onCreate = async () => {
        if(!selectedDomain || (!flowId && !isCampaign)) return;
        setIsLoading(true);
        try {
            if(isCampaign) {
                const campaignResponse = await createCampaign(selectedDomain, { title: formValues.title, active: formValues.isActive });
                const campaignId = campaignResponse?.data?.campaignId;
                if(campaignId) {
                    try {
                        const response = await createCampaignNotification(selectedDomain, campaignId, formValues);
                        if (response?.data?.status === 'ok' && response?.data?.id && response?.data?.campaignId) {
                            loadCampaigns();
                            onTemplateCreated(response.data.id, campaignId);
                        } else {
                            showToast({skin: ToastSkin.error, text: 'Failed to create new template'});
                        }
                    } catch (error) {
                        showToast({skin: ToastSkin.error, text: 'Failed to create new template'});
                    }
                } else {
                    showToast({ skin: ToastSkin.error, text: 'Failed to create new campaign' });
                }
            } else if(flowId) {
                const response = await createNotification(selectedDomain, flowId, formValues);
                if(response?.data?.status === 'ok' && response?.data?.id) {
                    loadFlows();
                    onTemplateCreated(response.data.id, flowId);
                } else {
                    showToast({ skin: ToastSkin.error, text: 'Failed to create new template'});
                }
            }
            setIsLoading(false);
        } catch (error) {
            console.log("error: ", error);
            showToast({ text: 'An unknown error occurred while saving your template'});
        }
    }

    useEffect(() => {
        const newFormValues = formValues;
        if(flowTriggerId && flowTriggerId > 1) {
            newFormValues.requiresConsent = true;
        }
        if(flowTriggerId === 11) {
            newFormValues.triggerDelay = 0;
        } else {
            newFormValues.triggerDelay = null;
        }
        setFormValues(newFormValues);
        setIsDefaultDataLoaded(true);
    }, [flowTriggerId]);

    if(!isDefaultDataLoaded) return null;
    return (
        <div className="one-step-template-creation-container">
            <div className="left">
                <Step1Fields
                    title={formValues.title}
                    setTitle={setTitle}
                    language={formValues.language}
                    setLanguage={setLanguage}
                    content={formValues.content}
                    setContent={setContent}
                    isWithImage={formValues.isWithImage}
                    setIsWithImage={setIsWithImage}
                    isCoupon={formValues.isCoupon}
                    setIsCoupon={setIsCoupon}
                    flowTriggerId={flowTriggerId}
                    requiresConsent={formValues.requiresConsent}
                    setRequiresConsent={setRequiresConsent}
                    direction={direction}
                    isCampaign={isCampaign}
                    setImageUrl={setImageUrl}
                    setIsLoading={setIsLoading}
                    button1Url={formValues.button1Url}
                    setButton1Url={setButton1Url}
                />
                {shouldAskForDelay ? <Step2Fields triggerDelay={formValues.triggerDelay} setTriggerDelay={setTriggerDelay} flowTriggerId={flowTriggerId} /> : null}
                {isCampaign ? null : <Step3Fields isActive={formValues.isActive} setIsActive={setIsActive} />}
            </div>
            <div className="right">
                <MessagePreview
                    text={formValues.content}
                    isWithImage={formValues.isWithImage}
                    flowTriggerId={flowTriggerId}
                    language={formValues.language}
                    button1Text={formValues.button1Text}
                    button2Text={formValues.button2Text}
                    setButton1Text={setButton1Text}
                    setButton2Text={setButton2Text}
                    imageUrl={formValues.imageUrl}
                />
                <div className="buttons">
                    <Button type="text" size="large" className="next-step-button" disabled={isLoading} onClick={onCancel} icon={<LongArrowLeft width={10} />}>Back</Button>
                    <Button type="primary" size="large" className="next-step-button" loading={isLoading} onClick={onCreate} icon={<ThunderboltFilled />} disabled={!formValues.title || !formValues.content || (shouldAskForDelay && !formValues.triggerDelay)}>Create Template</Button>
                </div>
            </div>
        </div>
    );
}

export default OneStepTemplateCreation;